import { useState, useEffect } from "react";
import { Send } from "lucide-react";
import SectionHead from "./SectionHead";
import { submitForm, fetchFormStats } from "../../axios";

const CATEGORIES = [
  { id: "tkti", label: "TKTI talabasi", needsGroup: true, groupLabel: "Guruh raqami", groupPlaceholder: "Masalan: 25/84" },
  { id: "otm", label: "Boshqa OTM talabasi", needsGroup: true, groupLabel: "OTM nomi va guruh", groupPlaceholder: "Masalan: TATU, AT-22" },
  { id: "school", label: "Maktab o'quvchisi", needsGroup: true, groupLabel: "Maktab va sinf", groupPlaceholder: "Masalan: 145-maktab, 11-sinf" },
  { id: "other", label: "Ishlayotgan / boshqa", needsGroup: false, groupLabel: "", groupPlaceholder: "" },
];

export default function Form({ f, formRef }) {
  const [form, setForm] = useState({});
  const [category, setCategory] = useState(null);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      const result = await fetchFormStats();
      if (result && result.success) {
        setStats(result);
      }
    };
    fetchStats();
  }, []);

  const selectedCat = CATEGORIES.find(c => c.id === category);

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const handleSubmit = async () => {
    if (stats?.is_full) {
      alert("Afsuski, barcha kvota o'rinlari to'lib bo'lgan!");
      return;
    }
    if (!form.ism || !form.telefon || !category) return;
    if (selectedCat?.needsGroup && !form.guruh_raqami) return;

    setLoading(true);

    const result = await submitForm({
      ism: form.ism,
      telefon: form.telefon,
      category: selectedCat?.label,
      guruh_raqami: form.guruh_raqami || "",
      izoh: form.izoh || "",
      sahifa: window.location.href,
    });

    if (result.success) {
      setSent(true);
      // Ariza muvaffaqiyatli topshirilgach statistika qayta yangilanadi
      const newStats = await fetchFormStats();
      if (newStats) setStats(newStats);
    } else {
      alert(result.error || "Xatolik yuz berdi, qaytadan urinib ko'ring.");
    }

    setLoading(false);
  };

  const isDisabled = loading
    || stats?.is_full
    || !form.ism
    || !form.telefon
    || !category
    || (selectedCat?.needsGroup && !form.guruh_raqami);

  return (
    <section className="py-20 bg-white" ref={formRef} id="apply">
      <div className="max-w-7xl mx-auto px-2">
        <SectionHead eyebrow={f.eyebrow} title={f.title} sub={f.subtitle} />

        <div className="dc-form-wrap">
          {sent ? (
            <div className="py-4 text-center">
              <div className="mb-4 text-5xl">{f.successIcon}</div>
              <div className="mb-2 text-xl font-extrabold text-gray-900">{f.successTitle}</div>
              <div className="text-sm leading-relaxed text-gray-500">{f.successSub}</div>
            </div>
          ) : (
            <>
              <p className="mb-4 text-center text-sm leading-relaxed text-gray-400">{f.formSub}</p>

              {/* STATISTIKA / KVOTA KO'RSATKICHI */}
              {stats && (
                <div className="mb-6 rounded-2xl border border-violet-100 bg-violet-50/50 p-4 text-center">
                  <div className="text-xs font-bold text-violet-700">
                    Qabul kvotasi: <span className="text-gray-900">{stats.total_applications} / {stats.max_kvota}</span>
                  </div>
                  <div className="mt-1 text-sm font-extrabold text-violet-600">
                    {stats.is_full ? (
                      <span className="text-red-500">🚨 Qabul kvotasi to'ldi!</span>
                    ) : (
                      <span>Qolgan o'rinlar: {stats.qolgan_joylar} ta</span>
                    )}
                  </div>
                </div>
              )}

              {/* Ism */}
              <div className="mb-5">
                <label className="mb-2 block text-xs font-bold text-gray-700">
                  {f.fields[0].label} <span className="text-violet-500">*</span>
                </label>
                <input
                  className="dc-form-input"
                  type={f.fields[0].type}
                  placeholder={f.fields[0].placeholder}
                  value={form.ism || ""}
                  onChange={e => set("ism", e.target.value)}
                />
              </div>

              {/* Kategoriya */}
              <div className="mb-5">
                <label className="mb-2 block text-xs font-bold text-gray-700">
                  Siz kim sifatida qatnashmoqchisiz? <span className="text-violet-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {CATEGORIES.map(cat => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => { setCategory(cat.id); set("guruh_raqami", ""); }}
                      className={[
                        "rounded-xl border-[1.5px] px-3 py-2.5 text-left text-xs font-bold transition-all",
                        category === cat.id
                          ? "border-violet-500 bg-violet-50 text-violet-700"
                          : "border-gray-200 bg-gray-50 text-gray-600 hover:border-violet-300 hover:bg-violet-50/50",
                      ].join(" ")}>
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Guruh */}
              {selectedCat?.needsGroup && (
                <div className="mb-5">
                  <label className="mb-2 block text-xs font-bold text-gray-700">
                    {selectedCat.groupLabel} <span className="text-violet-500">*</span>
                  </label>
                  <input
                    className="dc-form-input"
                    type="text"
                    placeholder={selectedCat.groupPlaceholder}
                    value={form.guruh_raqami || ""}
                    onChange={e => set("guruh_raqami", e.target.value)}
                  />
                </div>
              )}

              {/* Telefon */}
              <div className="mb-5">
                <label className="mb-2 block text-xs font-bold text-gray-700">
                  {f.fields[2].label} <span className="text-violet-500">*</span>
                </label>
                <input
                  className="dc-form-input"
                  type={f.fields[2].type}
                  placeholder={f.fields[2].placeholder}
                  value={form.telefon || ""}
                  onChange={e => set("telefon", e.target.value)}
                />
              </div>

              {/* Izoh */}
              <div className="mb-5">
                <label className="mb-2 block text-xs font-bold text-gray-700">
                  {f.fields[3].label}{" "}
                  <span className="font-normal text-gray-400">(bog'lanish)</span>
                </label>
                <textarea
                  className="dc-form-input"
                  placeholder={f.fields[3].placeholder}
                  rows={3}
                  style={{ resize: "vertical" }}
                  value={form.izoh || ""}
                  onChange={e => set("izoh", e.target.value)}
                />
              </div>

              {/* Submit */}
              <button
                onClick={handleSubmit}
                disabled={isDisabled}
                className="mt-1 w-full rounded-2xl py-3.5 text-base font-extrabold text-white transition-all"
                style={{
                  background: isDisabled ? "#c4b5fd" : "#7c3aed",
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  boxShadow: isDisabled ? "none" : "0 4px 16px rgba(124,58,237,0.3)",
                }}>
                {loading ? f.loadingBtn : stats?.is_full ? "Qabul to'lgan" : `${f.submitBtn} →`}
              </button>

              {/* OR divider */}
              <div className="dc-or-divider">
                <div className="dc-or-line" />
                <span className="text-xs font-semibold" style={{ color: "#c4b5fd" }}>
                  yoki xatolik yuz bersa
                </span>
                <div className="dc-or-line" />
              </div>

              {/* Telegram link */}
              <a
                href={f.telegramLink}
                target="_blank"
                rel="noreferrer"
                className="flex w-full items-center justify-center gap-2 rounded-2xl py-3.5 text-sm font-bold text-blue-600 no-underline transition-all hover:-translate-y-0.5"
                style={{ background: "#f0f9ff", border: "1.5px solid #bae6fd" }}>
                <Send size={16} />
                {f.telegramBtn}
              </a>

              <p className="mt-4 text-center text-xs text-gray-400">{f.note}</p>
            </>
          )}
        </div>
      </div>
    </section>
  );
}